"use client"

import { deleteCookie, getCookie, setCookie } from 'cookies-next/client';
import { gql } from 'graphql-request';
import { usePathname } from 'next/navigation';
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { useFilminfo } from 'src/lib/client/useFilminfo';
import useIsHydrated from 'src/lib/client/useIsHydrated';
import { SelectedCinemaContext, useLocationContext } from "src/lib/contexts/ContextTypes";
import type { Cinema } from 'src/lib/movieinfo/movieinfotypes';
import { buildProgramHash, parseProgramHash, PROGRAM_FILTER_COOKIE_MAX_AGE, SELECTED_CINEMAS_COOKIE as CINEMAS_COOKIE, updateProgramHash } from 'src/lib/programHash';




//#region [Props]
type SelectedCinemaWrapperProps = {
    predefinedCinema?: string;
    children: React.ReactNode;
};
//#endregion

//#region [Component]
export default function SelectedCinemaWrapper({ children, predefinedCinema }: SelectedCinemaWrapperProps) {
    const [selectedCinemas, dispatch] = useReducer(selectedCinemaReducer, predefinedCinema ? [predefinedCinema] : null);
    const locationContext = useLocationContext();
    const [isReady, setIsReady] = useState<boolean>(false);

    const { fiLoading, fiData } = useFilminfo(CINEMA_QUERY, { locations: locationContext.location }, { active: (locationContext.location ?? []).length > 0 });

    // isReady is derived from location/fetch state and only ever flips false -> true,
    // so it's set during render (React's "adjust state while rendering" pattern)
    // instead of in an effect, avoiding an extra cascading render.
    const shouldBeReady = locationContext.isReady || (!fiLoading && !!fiData);
    if (shouldBeReady && !isReady) {
        setIsReady(true);
    }

    // availableCinemas is fully derived from the fetch result, so it doesn't need its own state.
    // Memoized so the reference is stable across renders where fiData/fiLoading haven't
    // changed — the effect below depends on it and would otherwise re-run every render.
    const availableCinemas = useMemo(() => (!fiLoading && fiData?.cinemaQuery?.getCinemas?.length)
        ? fiData.cinemaQuery!.getCinemas as Cinema[]
        : [], [fiLoading, fiData]);

    // Only reacts to predefinedCinema changing (e.g. navigating to a different fixed-cinema page),
    // not on mount, where it would overwrite the cinemas restored from the hash. Must NOT react to
    // fiData/fiLoading, since those change on every cinema refetch (e.g. a location being removed),
    // which would wipe out the user's selection instead of letting the pruning effect below trim it.
    const [prevPredefinedCinema, setPrevPredefinedCinema] = useState(predefinedCinema);
    if (predefinedCinema !== prevPredefinedCinema) {
        setPrevPredefinedCinema(predefinedCinema);
        dispatch({ type: "replace", cinemas: predefinedCinema ? [predefinedCinema] : null });
    }

    // A new set of cities always clears the chosen cinemas, also when the cities they belong to are still included.
    // null until the location is ready, so the first ready location isn't treated as a change
    // (it would wipe the cinemas restored from the hash or cookie).
    const locationKey = locationContext.isReady ? (locationContext.location ?? []).join("|") : null;
    const [prevLocationKey, setPrevLocationKey] = useState(locationKey);
    if (locationKey !== prevLocationKey) {
        setPrevLocationKey(locationKey);
        if (prevLocationKey !== null) {
            dispatch({ type: "replace", cinemas: predefinedCinema ? [predefinedCinema] : null });
        }
    }

    // The hash and cookie are only known in the browser, so they're read in the first render after
    // hydration to avoid a hydration mismatch. The hash wins; the cookie keeps the cinemas when coming
    // from a link without them (e.g. back to the program from a film page, or a later visit).
    // A predefined cinema always wins, and then the hash and cookie are neither read nor written.
    // Waits for the location: LocationContext wipes the cookie when a new location is saved (e.g. from
    // ?location=), and that happens in an effect, after the first render. (null = no LocationWrapper)
    const isHydrated = useIsHydrated();
    const [isRestoredFromHash, setIsRestoredFromHash] = useState(false);
    if (isHydrated && locationContext.isReady !== false && !isRestoredFromHash) {
        setIsRestoredFromHash(true);
        const cinemas = parseProgramHash(window.location.hash).cinemas
            ?? parseProgramHash(getCookie(CINEMAS_COOKIE) ?? "").cinemas;
        if (cinemas && !predefinedCinema) {
            dispatch({ type: "replace", cinemas });
        }
    }

    // pathname: the wrapper lives on in layouts across client navigations, and the new page's
    // hash (e.g. a link with only the date) doesn't have the cinemas yet
    const pathname = usePathname();
    useEffect(() => {
        // not before the hash is read - writing the initial empty selection would wipe it
        if (!isRestoredFromHash || predefinedCinema) {
            return;
        }
        updateProgramHash({ cinemas: selectedCinemas });
        if (selectedCinemas?.length) {
            // same format as the hash, without the "#". Kept across visits.
            setCookie(CINEMAS_COOKIE, buildProgramHash({ date: null, cinemas: selectedCinemas, genres: null }).slice(1), { path: "/", maxAge: PROGRAM_FILTER_COOKIE_MAX_AGE });
        } else {
            deleteCookie(CINEMAS_COOKIE, { path: "/" });
        }
    }, [selectedCinemas, isRestoredFromHash, predefinedCinema, pathname]);

	useEffect(() => {
		// fiLoading must also gate this: while a location-driven refetch is in flight,
		// availableCinemas is transiently [] (react-query has no data yet for the new
		// queryKey). Pruning against that empty list would wipe the whole selection
		// instead of waiting for the real, settled list to trim just the stale entries.
		if (!isReady || fiLoading) {
			return;
		}
		const filteredSelectedCinemas = selectedCinemas?.filter(celCin => !!availableCinemas.find((ac) => ac.name === celCin)) ?? [];
		// filter() can only remove entries, so a length mismatch is enough to detect
		// a real change — dispatching unconditionally would create a new array reference
		// on every run, re-triggering this effect (which depends on selectedCinemas) forever.
		if (filteredSelectedCinemas.length !== (selectedCinemas?.length ?? 0)) {
			dispatch({type: "replace", cinemas: filteredSelectedCinemas});
		}
	}, [availableCinemas, selectedCinemas, isReady, fiLoading]);

    return <SelectedCinemaContext.Provider value={
        {
            selectedCinemas: selectedCinemas,
            setSelectedCinemas: (cinemas) => dispatch({ type: "replace", cinemas }),
            addSelectedCinema: (cinema) => dispatch({ type: "add", cinema }),
            removeSelectedCinema: (cinema) => dispatch({ type: "remove", cinema }),
            resetSelectedCinemas: () => dispatch({ type: "replace", cinemas: predefinedCinema ? [predefinedCinema] : null }),
            availableCinemas: availableCinemas,
            isReady: isReady

        }
    }>
        {children}
    </SelectedCinemaContext.Provider>;
}
//#endregion

//#region [Other]

type CinemaAction = CinemaAddAction | CinemaRemoveAction | CinemaReplaceAction;

type CinemaAddAction = {
    type: "add";
    cinema: string;
};

type CinemaRemoveAction = {
    type: "remove";
    cinema: string;
};

type CinemaReplaceAction = {
    type: "replace",
    cinemas: string[] | null;
};

function selectedCinemaReducer(state: string[] | null, action: CinemaAction) {
    let cinemas: string[] | null = state ?? [];
    switch (action.type) {
        case "add":
            if (!cinemas.includes(action.cinema)) {
                cinemas = [...cinemas, action.cinema]; // return a new object
            }
            break;
        case "remove":
            cinemas = cinemas.filter(cin => cin !== action.cinema); // new object
            break;
        case "replace":
            cinemas = action.cinemas;
            break;
    }
    if (cinemas?.length === 0) {
        cinemas = null;
    }
    return cinemas;
}
//#endregion

//#region [Other]
const CINEMA_QUERY = gql`query ($locations: [String]) {
		cinemaQuery {
			getCinemas (locations: $locations) {
				name
				firmId
			}
		}
	}`;

//#endregion

//#region [Other]

//#endregion